/**
 * What a word refers back to in the dialogue (Requirement 62): in "Tu veux
 * les pommes ? — Oui, j'en veux", en is "les pommes". That depends on this
 * video, not on the language, so it lives on hoja's annotation:
 *
 *   refersTo: { subtitleId, wordIndices: [2, 3], text: 'les pommes', startTime }
 *
 * The words are picked in the annotation's own subtitle or one before it.
 * `text` keeps what was picked, so the link still reads right if the
 * subtitle is edited; `startTime` lets students go back to hear it.
 */
import { wordsOf, normalWord } from './wordForWord';
import { subtitleFor } from './timeline';

const byStart = (a, b) => a.startTime - b.startTime;

/** How many subtitles before its own a word can refer back to. */
export const REFERENCE_REACH = 3;

/**
 * The subtitles an annotation's word can refer to, earliest first: its own
 * (the one it was tapped in, else the one its time belongs to) and up to
 * REFERENCE_REACH before it.
 */
export function referenceCandidates(annotation, subtitles = [], reach = REFERENCE_REACH) {
  const sorted = [...subtitles].filter((s) => wordsOf(s.originalText).length > 0).sort(byStart);
  const own = (annotation.subtitleId && sorted.find((s) => s.id === annotation.subtitleId))
    || subtitleFor(annotation.startTime, sorted);
  if (!own) return [];
  const i = sorted.indexOf(own);
  return sorted.slice(Math.max(0, i - reach), i + 1);
}

/** The link to save for these words of a subtitle; null when none picked. */
export function makeReference(subtitle, wordIndices) {
  const words = wordsOf(subtitle && subtitle.originalText);
  const picked = [...new Set(wordIndices)].filter((i) => i >= 0 && i < words.length).sort((a, b) => a - b);
  if (picked.length === 0) return null;
  return {
    subtitleId: subtitle.id,
    wordIndices: picked,
    text: picked.map((i) => words[i]).join(' ').replace(/^[^\p{L}\p{N}']+|[^\p{L}\p{N}']+$/gu, ''),
    startTime: subtitle.startTime,
  };
}

/** Whether an annotation has a usable link. */
export const hasReference = (a) => !!(a && a.refersTo && typeof a.refersTo.text === 'string' && a.refersTo.text.trim());

/**
 * Where the linked words are in this subtitle now: the saved places while
 * they still hold the same words, else the first run matching the saved
 * text, else [] (the subtitle was changed: only the text is shown).
 */
export function referenceWordsIn(ref, subtitle) {
  if (!ref || !subtitle || ref.subtitleId !== subtitle.id) return [];
  const words = wordsOf(subtitle.originalText);
  const normal = words.map(normalWord);
  const target = wordsOf(ref.text).map(normalWord).filter(Boolean);
  if (target.length === 0) return [];
  const saved = Array.isArray(ref.wordIndices) ? ref.wordIndices : [];
  if (saved.length === target.length && saved.every((i, k) => normal[i] === target[k])) return saved;
  for (let i = 0; i + target.length <= normal.length; i += 1) {
    if (target.every((w, k) => normal[i + k] === w)) return target.map((_, k) => i + k);
  }
  return [];
}
